import { useMemo, useState } from "react";

import { useTheme } from "next-themes";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { AdminLoginCard } from "@/components/admin/AdminLoginCard";
import { NumerolojiAdminMobileSidebar, NumerolojiAdminSidebar } from "@/components/admin/NumerolojiAdminSidebar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { adminFetch, useAdminSession } from "@/hooks/use-admin-session";

import { Activity, Bell, BookOpen, Footprints, LayoutGrid, List, PencilRuler, Search, Sparkles, Sprout, Star, Trash2 } from "lucide-react";

type CatalogActivity = {
  id: string;
  title: string;
  description: string;
  metaLabel?: string;
  metaValue?: string;
  durationMinutes?: number;
  difficulty?: "easy" | "medium" | "hard";
  coverImageUrl?: string;
  iconKey?: string;
  featured?: boolean;
  active: boolean;
  tags: string[];
};

type CatalogResponse = {
  ok: true;
  packages: unknown[];
  products: unknown[];
  meditations: unknown[];
  diets: unknown[];
  activities: CatalogActivity[];
  templates: unknown[];
  settings: unknown;
};

async function fetchCatalog(): Promise<CatalogResponse> {
  const res = await adminFetch("/api/admin/numeroloji/catalog");
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Katalog alınamadı.");
  }
  return json as CatalogResponse;
}

async function saveActivity(payload: Partial<CatalogActivity> & { title: string }) {
  const res = await adminFetch("/api/admin/numeroloji/activities", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktivite kaydedilemedi.");
  }
  return json as { ok: true; item: CatalogActivity };
}

async function deleteActivity(id: string) {
  const res = await adminFetch(`/api/admin/numeroloji/activities/${encodeURIComponent(id)}/delete`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
  });
  const json = (await res.json().catch(() => null)) as any;
  if (!res.ok) {
    throw new Error(typeof json?.error === "string" ? json.error : "Aktivite silinemedi.");
  }
  return json as { ok: true; deleted: boolean };
}

function iconFor(key?: string) {
  const k = String(key ?? "").toLowerCase();
  if (k === "walk") return Footprints;
  if (k === "meditation") return Sparkles;
  if (k === "learning") return BookOpen;
  if (k === "nutrition") return Sprout;
  return Activity;
}

function difficultyLabel(v?: string) {
  if (v === "hard") return "Zor";
  if (v === "medium") return "Orta";
  if (v === "easy") return "Kolay";
  return "—";
}

function normalizeTag(value: string) {
  return String(value ?? "")
    .trim()
    .toLocaleLowerCase("tr-TR")
    .replace(/\s+/g, "")
    .replace(/[ç]/g, "c")
    .replace(/[ğ]/g, "g")
    .replace(/[ı]/g, "i")
    .replace(/[ö]/g, "o")
    .replace(/[ş]/g, "s")
    .replace(/[ü]/g, "u")
    .replace(/[^0-9a-z]+/g, "");
}

function hasRituelTag(tags: string[]) {
  const values = new Set((tags ?? []).map((tag) => normalizeTag(tag)).filter(Boolean));
  return values.has("rituel") || values.has("ritueluygulamasi") || values.has("ritual");
}

export default function AdminNumerolojiActivities() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isRituelMode = pathname.startsWith("/admin/numeroloji/rituel");
  const queryClient = useQueryClient();

  const { theme, setTheme, systemTheme } = useTheme();
  const resolvedTheme = theme === "system" ? systemTheme : theme;
  const isDark = resolvedTheme === "dark";

  const { sessionQuery, loginMutation, logoutMutation } = useAdminSession();
  const configured = Boolean(sessionQuery.data?.configured);
  const authenticated = Boolean(sessionQuery.data?.authenticated);

  const catalogQuery = useQuery({
    queryKey: ["admin-numeroloji-catalog"],
    queryFn: fetchCatalog,
    enabled: authenticated,
    staleTime: 0,
  });

  const activities = useMemo(() => {
    const all = catalogQuery.data?.activities ?? [];
    return all.filter((activity) => (isRituelMode ? hasRituelTag(activity.tags ?? []) : !hasRituelTag(activity.tags ?? [])));
  }, [catalogQuery.data, isRituelMode]);

  const saveMutation = useMutation({
    mutationFn: (payload: any) => saveActivity(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteActivity(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin-numeroloji-catalog"] });
    },
  });

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<"all" | "active" | "inactive">("all");
  const [sort, setSort] = useState<"newest" | "title-asc" | "featured-first">("newest");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();

    let rows = activities;

    if (q) {
      rows = rows.filter((a) => {
        const hay = `${a.title} ${a.description} ${a.metaLabel ?? ""} ${a.metaValue ?? ""} ${(a.tags ?? []).join(" ")}`.toLowerCase();
        return hay.includes(q);
      });
    }

    if (filter === "active") rows = rows.filter((a) => a.active);
    if (filter === "inactive") rows = rows.filter((a) => !a.active);

    const sorted = [...rows];
    if (sort === "title-asc") {
      sorted.sort((x, y) => String(x.title || "").localeCompare(String(y.title || ""), "tr"));
    } else if (sort === "featured-first") {
      sorted.sort((x, y) => Number(Boolean(y.featured)) - Number(Boolean(x.featured)) || String(x.title || "").localeCompare(String(y.title || ""), "tr"));
    }
    // "newest" = server order (created_at DESC from hydration)

    return sorted;
  }, [activities, filter, search, sort]);

  if (sessionQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Yükleniyor...</div>;
  }

  if (!authenticated) {
    return (
      <AdminLoginCard
        configured={configured}
        onLogin={(args) => loginMutation.mutate(args)}
        isLoading={loginMutation.isPending}
        error={loginMutation.isError ? (loginMutation.error as Error).message : undefined}
      />
    );
  }

  return (
    <div className="flex min-h-dvh w-full">
      <NumerolojiAdminSidebar mode="links" isDark={isDark} onToggleTheme={() => setTheme(isDark ? "light" : "dark")} />

      <div className="flex min-w-0 flex-1 flex-col bg-slate-100/60 dark:bg-slate-900/30">
        <header className="h-16 border-b border-slate-200/70 bg-white/70 px-4 backdrop-blur dark:border-slate-800/60 dark:bg-slate-950/40 sm:px-6">
          <div className="flex h-full items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <NumerolojiAdminMobileSidebar
                mode="links"
                isDark={isDark}
                onToggleTheme={() => setTheme(isDark ? "light" : "dark")}
              />
              <div className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-50 sm:text-lg">{isRituelMode ? "Ritüel Yönetimi" : "Aktivite Yönetimi"}</div>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="button"
                className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                aria-label="Bildirimler"
              >
                <Bell className="h-4 w-4" />
              </button>

              <Button
                variant="outline"
                className="rounded-full"
                size="sm"
                onClick={() => logoutMutation.mutate()}
                disabled={logoutMutation.isPending}
              >
                Çıkış
              </Button>
            </div>
          </div>
        </header>

        <div className="min-w-0 flex-1 p-4 sm:p-6">
          {catalogQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Yükleniyor...</div>
          ) : catalogQuery.isError ? (
            <div className="text-sm text-destructive">{(catalogQuery.error as Error).message}</div>
          ) : (
            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-950/40">
              <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{isRituelMode ? "Ritüeller" : "Aktiviteler"}</div>
                  <div className="mt-2 max-w-2xl text-sm text-slate-500 dark:text-slate-400">
                    {isRituelMode
                      ? "Panelde gösterilecek ritüelleri burada yönetebilirsiniz."
                      : "Panelde gösterilecek aktiviteleri burada yönetebilirsiniz."}
                  </div>
                </div>

                <div className="flex flex-wrap gap-3">
                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-300">
                      <div className="h-2 w-2 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Aktif</div>
                      <div className="text-xl font-extrabold">{activities.filter((a) => a.active).length}</div>
                    </div>
                  </div>

                  <div className="flex min-w-[160px] items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950/40">
                    <div className="grid h-10 w-10 place-items-center rounded-xl bg-yellow-500/10 text-yellow-700 dark:text-yellow-300">
                      <Star className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Öne çıkan</div>
                      <div className="text-xl font-extrabold">{activities.filter((a) => a.featured).length}</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input
                    className="h-11 w-full rounded-2xl bg-slate-100 pl-10 pr-4 text-sm text-slate-900 outline-none ring-blue-600/20 focus:ring-4 dark:bg-slate-800 dark:text-slate-50"
                    placeholder="Başlık, açıklama, etiket..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    variant={filter === "all" ? "default" : "outline"}
                    className={cn("rounded-full", filter !== "all" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setFilter("all")}
                  >
                    Tümü
                  </Button>
                  <Button
                    size="sm"
                    variant={filter === "active" ? "default" : "outline"}
                    className={cn("rounded-full", filter !== "active" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setFilter("active")}
                  >
                    Aktif
                  </Button>
                  <Button
                    size="sm"
                    variant={filter === "inactive" ? "default" : "outline"}
                    className={cn("rounded-full", filter !== "inactive" && "bg-white dark:bg-slate-950/40")}
                    onClick={() => setFilter("inactive")}
                  >
                    Pasif
                  </Button>

                  <div className="ml-0 flex items-center gap-2 lg:ml-2">
                    <div className="text-xs font-medium text-slate-500 dark:text-slate-400">Sırala:</div>
                    <select
                      className="h-10 rounded-full border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 outline-none dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-200"
                      value={sort}
                      onChange={(e) => setSort(e.target.value as any)}
                    >
                      <option value="newest">En Yeni</option>
                      <option value="title-asc">Başlık (A-Z)</option>
                      <option value="featured-first">Öne çıkan</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-1 rounded-2xl border border-slate-200 bg-white p-1 dark:border-slate-800 dark:bg-slate-950/40">
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "grid" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("grid")}
                      title="Grid görünümü"
                    >
                      <LayoutGrid className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      className={cn("grid h-8 w-8 place-items-center rounded-xl transition-colors", viewMode === "list" ? "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900" : "text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100")}
                      onClick={() => setViewMode("list")}
                      title="Liste görünümü"
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  <Button
                    size="sm"
                    className="rounded-full"
                    onClick={() => navigate(isRituelMode ? "/admin/numeroloji/rituel/yeni" : "/admin/numeroloji/aktivite/yeni")}
                  >
                    <span className="mr-1">+</span>
                    {isRituelMode ? "Yeni ritüel" : "Yeni aktivite"}
                  </Button>
                </div>
              </div>

              {saveMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(saveMutation.error as Error).message}
                </div>
              ) : null}

              {deleteMutation.isError ? (
                <div className="mt-4 rounded-2xl border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {(deleteMutation.error as Error).message}
                </div>
              ) : null}

              {viewMode === "grid" ? (
                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                  {visible.map((a) => {
                    const Icon = iconFor(a.iconKey);
                    const badge = a.active ? "Aktif" : "Pasif";
                    const badgeClass = a.active ? "bg-emerald-500" : "bg-slate-500";

                    return (
                      <div
                        key={a.id}
                        className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-lg dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className="relative aspect-[4/3] bg-gradient-to-br from-indigo-600/20 via-blue-600/10 to-slate-950/20">
                          {a.coverImageUrl ? (
                            <img src={a.coverImageUrl} alt={a.title} className="absolute inset-0 h-full w-full object-cover" />
                          ) : null}

                          <div
                            className={cn(
                              "absolute left-3 top-3 rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-white",
                              badgeClass,
                            )}
                          >
                            {badge}
                          </div>

                          {a.featured ? (
                            <div className="absolute left-3 bottom-3 rounded-full bg-yellow-500/20 px-2 py-1 text-[10px] font-black uppercase tracking-wider text-yellow-200">
                              Öne çıkan
                            </div>
                          ) : null}

                          <div className="absolute right-3 top-3 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:text-slate-200 dark:ring-slate-800"
                              onClick={() =>
                                navigate(
                                  isRituelMode
                                    ? `/admin/numeroloji/rituel/${encodeURIComponent(a.id)}`
                                    : `/admin/numeroloji/aktivite/${encodeURIComponent(a.id)}`,
                                )
                              }
                            >
                              <PencilRuler className="h-4 w-4" />
                            </button>
                            <button
                              type="button"
                              className="grid h-9 w-9 place-items-center rounded-2xl bg-white/90 text-rose-600 shadow-sm ring-1 ring-slate-200/60 backdrop-blur hover:bg-white dark:bg-slate-950/80 dark:ring-slate-800"
                              disabled={deleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu aktiviteyi silmek istiyor musunuz?");
                                if (!ok) return;
                                deleteMutation.mutate(a.id);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>

                          <div className="absolute bottom-3 right-3 rounded-2xl bg-white/70 px-3 py-2 text-xs font-semibold text-slate-900 backdrop-blur dark:bg-slate-950/60 dark:text-slate-50">
                            <Icon className="mr-2 inline-block h-4 w-4" />
                            {a.metaValue ?? (a.durationMinutes ? `${a.durationMinutes} dk` : difficultyLabel(a.difficulty))}
                          </div>
                        </div>

                        <div className="p-5">
                          <div className="text-base font-bold text-slate-900 dark:text-white">{a.title}</div>
                          <div className="mt-3 line-clamp-3 text-sm text-slate-600 dark:text-slate-300">{a.description}</div>

                          <div className="mt-4 flex items-center justify-between gap-3">
                            <div className="flex flex-wrap gap-1">
                              {(a.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span
                                  key={`${a.id}-tag-${idx}`}
                                  className="rounded-full bg-blue-600/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300"
                                >
                                  {t}
                                </span>
                              ))}
                            </div>

                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={a.active}
                                disabled={saveMutation.isPending}
                                onChange={() =>
                                  saveMutation.mutate({
                                    ...a,
                                    active: !a.active,
                                  })
                                }
                              />
                              <div className="h-6 w-11 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full dark:bg-slate-800" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate(isRituelMode ? "/admin/numeroloji/rituel/yeni" : "/admin/numeroloji/aktivite/yeni")}
                    className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-slate-200 bg-white p-6 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300 dark:hover:bg-slate-900/30"
                  >
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-2xl">+</span>
                    </div>
                    <div className="mt-3 text-sm font-bold">{isRituelMode ? "Yeni ritüel ekle" : "Yeni aktivite ekle"}</div>
                    <div className="mt-1 text-center text-xs text-slate-500 dark:text-slate-400">
                      {isRituelMode ? "Yeni bir ritüel planı oluştur." : "Yeni bir aktivite planı oluştur."}
                    </div>
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {visible.map((a) => {
                    const Icon = iconFor(a.iconKey);
                    const badge = a.active ? "Aktif" : "Pasif";
                    const badgeClass = a.active ? "bg-emerald-500" : "bg-slate-500";

                    return (
                      <div
                        key={a.id}
                        className="group flex overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-800 dark:bg-slate-950/40"
                      >
                        <div className="relative w-24 flex-none bg-gradient-to-br from-indigo-600/20 via-blue-600/10 to-slate-950/20 sm:w-36">
                          {a.coverImageUrl ? (
                            <img src={a.coverImageUrl} alt={a.title} className="absolute inset-0 h-full w-full object-cover" />
                          ) : null}
                          <div className={cn("absolute left-2 top-2 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white", badgeClass)}>
                            {badge}
                          </div>
                          {a.featured ? (
                            <div className="absolute bottom-2 left-2 rounded-full bg-yellow-500/20 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-yellow-200">
                              ★
                            </div>
                          ) : null}
                        </div>

                        <div className="flex flex-1 items-center gap-3 px-4 py-3">
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-bold text-slate-900 dark:text-white">{a.title}</div>
                            <div className="mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{a.description}</div>
                            <div className="mt-1.5 flex flex-wrap gap-1">
                              {(a.tags ?? []).slice(0, 3).map((t, idx) => (
                                <span key={`${a.id}-tag-${idx}`} className="rounded-full bg-blue-600/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-300">
                                  {t}
                                </span>
                              ))}
                            </div>
                          </div>

                          <div className="hidden flex-none sm:block">
                            <div className="rounded-xl bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                              <Icon className="mr-1.5 inline-block h-3.5 w-3.5" />
                              {a.metaValue ?? (a.durationMinutes ? `${a.durationMinutes} dk` : difficultyLabel(a.difficulty))}
                            </div>
                          </div>

                          <div className="flex flex-none items-center gap-2">
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                              onClick={() =>
                                navigate(
                                  isRituelMode
                                    ? `/admin/numeroloji/rituel/${encodeURIComponent(a.id)}`
                                    : `/admin/numeroloji/aktivite/${encodeURIComponent(a.id)}`,
                                )
                              }
                            >
                              <PencilRuler className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              className="grid h-8 w-8 place-items-center rounded-xl bg-slate-100 text-rose-600 hover:bg-rose-50 dark:bg-slate-800 dark:hover:bg-rose-900/20"
                              disabled={deleteMutation.isPending}
                              onClick={() => {
                                const ok = confirm("Bu aktiviteyi silmek istiyor musunuz?");
                                if (!ok) return;
                                deleteMutation.mutate(a.id);
                              }}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                            <label className="relative inline-flex cursor-pointer items-center">
                              <input
                                type="checkbox"
                                className="peer sr-only"
                                checked={a.active}
                                disabled={saveMutation.isPending}
                                onChange={() => saveMutation.mutate({ ...a, active: !a.active })}
                              />
                              <div className="h-5 w-9 rounded-full bg-slate-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-[16px] dark:bg-slate-700" />
                            </label>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={() => navigate(isRituelMode ? "/admin/numeroloji/rituel/yeni" : "/admin/numeroloji/aktivite/yeni")}
                    className="flex items-center gap-4 rounded-2xl border-2 border-dashed border-slate-200 bg-white px-6 py-4 text-slate-600 transition-colors hover:border-blue-500/50 hover:bg-blue-50/50 dark:border-slate-800 dark:bg-slate-950/40 dark:text-slate-300"
                  >
                    <div className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-slate-100 text-slate-900 dark:bg-slate-800 dark:text-slate-50">
                      <span className="text-xl">+</span>
                    </div>
                    <div className="text-sm font-bold">{isRituelMode ? "Yeni ritüel ekle" : "Yeni aktivite ekle"}</div>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
